import { useSuspenseQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  ContextLine,
  CurrencyAmountUI,
  DirectionIcon,
  FXTradeCard,
  Heading,
  Label,
  PrimaryFullScreenSpinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TransactionStatusUI,
  Txt,
  transactionTypeToBadgeLabels,
  UnitsUI,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { FC, ReactNode } from "react";
import { Suspense } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { PiBuildingOffice } from "react-icons/pi";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AdminTransactionDetailFragment,
  AssetCardFieldsFragment,
  BeneficiaryAssetCardFieldsFragment,
  FxTradeCardFieldsFragment,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/transactions_/$transactionId")({
  head: () => opsHead("Transaction"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { transactionId } = Route.useParams();
  const txnId = transactionId;

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransactionIdPage($id: ID!) {
        node(id: $id) {
          ...on Transaction {
            ...AdminTransactionDetail
          }
        }
      }

      fragment AdminTransactionDetail on Transaction {
        id
        createdAt
        type
        direction
        description
        shortRef
        reference
        currencyAmount {
          currency
          amount
        }
        effectiveAt
        status
        unitAmount {
          unit
          amount
        }

        ledgerAccount {
          ...on AssetLedgerAccount {
            asset {
              ...AssetCardFields
              businessEntity {
                id
                name
                organisation {
                  id
                  name
                  squareLogoUrl
                }
              }
            }
          }
        }

        fundMovement {
          ...on Payout {
            id
            businessEntity {
              id
              name
              organisation {
                id
                name
                squareLogoUrl
              }
            }
            sourceAsset {
              ...AssetCardFields
            }
            beneficiaryAccount {
              ...BeneficiaryAssetCardFields
            }
          }
          ...on Deposit {
            id
            destinationAsset {
              ...AssetCardFields
              businessEntity {
                id
                name
                organisation {
                  id
                  name
                  squareLogoUrl
                }
              }
            }
          }
          ...on Transfer {
            id
            sourceAsset {
              ...AssetCardFields
            }
            destinationAsset {
              ...AssetCardFields
            }
          }
          ... on EburyFXTrade {
            id
            ...FXTradeCardFields
          }
        }
      }
  `),
    {
      variables: {
        id: txnId,
      },
    },
  );

  const txn = data.node;
  if (!txn || txn.__typename !== "Transaction") {
    throw new NotFoundError();
  }

  const {
    id,
    currencyAmount,
    unitAmount,
    direction,
    reference,
    shortRef,
    status,
    description,
  } = txn;

  useOpsTitle(`Transaction #${shortRef}`);

  const formattedEffectiveAt = formatLocalDateTime(txn.effectiveAt);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={
          <div className="flex flex-row justify-between gap-2">
            {unitAmount && (
              <TooltipProvider delayDuration={50}>
                <Tooltip>
                  <TooltipTrigger>
                    <HiOutlineInformationCircle />
                  </TooltipTrigger>
                  <TooltipContent>
                    {unitAmount && (
                      <UnitsUI
                        unitAmount={unitAmount.amount.mul(
                          direction === "DEBIT" ? -1 : 1,
                        )}
                      />
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <div className="w-full text-right">
              <span className="flex flex-row justify-end gap-1 ">
                <CurrencyAmountUI
                  amount={currencyAmount.amount.mul(
                    direction === "DEBIT" ? -1 : 1,
                  )}
                  currency={currencyAmount.currency}
                  size="3xl"
                />
              </span>
            </div>
          </div>
        }
        contextLine={
          <ContextLine
            current={`#${shortRef}`}
            ancestors={[{ label: "Transaction", path: "/transactions" }]}
          />
        }
      />

      <div className="flex flex-col gap-10 rounded-lg border border-zinc-200 bg-white p-12 shadow-sm">
        <Heading as="h2" size="lg">
          Transaction details
        </Heading>
        <div className="flex w-full flex-col gap-0">
          <DetailRow
            label="Status"
            value={<TransactionStatusUI status={status} key="status" />}
          />
          {!unitAmount ? (
            <DetailRow
              label="Amount"
              value={
                <div className="flex flex-row gap-1">
                  <CurrencyAmountUI
                    amount={currencyAmount.amount}
                    currency={currencyAmount.currency}
                    showSuperscript={false}
                  />
                  <DirectionIcon direction={direction} />
                </div>
              }
            />
          ) : (
            <DetailRow
              label="Units"
              value={
                <UnitsUI unitAmount={unitAmount.amount} showSign={false} />
              }
            />
          )}

          <DetailRow label="On" value={formattedEffectiveAt} />
          <DetailRow label="Asset" value={getAssetFlow(txn)} />

          <DetailRow
            label="Transaction number"
            value={<span className="font-mono">{`#${shortRef}`}</span>}
          />
          <DetailRow label="Description" value={description} />
          <DetailRow label="Reference" value={reference} />
          <DetailRow
            label="Business Entity"
            value={getBusinessEntityCard(txn)}
          />
        </div>
        <div className="flex flex-row gap-2">
          <ViewParentButton fundMovement={txn.fundMovement ?? undefined} />

          <Link
            to={`/ledger/transactions/${id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Button className="w-fit" variant="outline-gray">
              View ledger transaction
            </Button>
          </Link>
        </div>
      </div>
    </AdminPageContainer>
  );
}

const DetailRow: FC<{
  label: ReactNode;
  value: ReactNode;
}> = ({ label, value }) =>
  value ? (
    <div className="flex w-full border-b border-b-secondary py-4">
      <div className="w-40">
        <Label>{label}</Label>
      </div>
      <div className="text-sm leading-6">{value}</div>
    </div>
  ) : null;

function ViewParentButton({
  fundMovement,
}: {
  fundMovement:
    | {
        id: string;
        __typename: "Deposit" | "Transfer" | "Payout" | "EburyFXTrade";
      }
    | undefined;
}) {
  if (!fundMovement) {
    return null;
  }

  switch (fundMovement.__typename) {
    case "Payout":
      return (
        <Link
          to={`/payouts/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline-gray">
            View payout
          </Button>
        </Link>
      );
    case "Transfer":
      return (
        <Link
          to={`/transfers/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline-gray">
            View transfer
          </Button>
        </Link>
      );
    case "Deposit":
      return (
        <Link
          to={`/deposits/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline-gray">
            View deposit
          </Button>
        </Link>
      );
    case "EburyFXTrade":
      return (
        <Link
          to={`/fx-trades/${fundMovement.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Button className="w-fit" variant="outline-gray">
            View FX Trade
          </Button>
        </Link>
      );
    default:
      return null;
  }
}

const formatLocalDateTime = (localDate: Date) => {
  const dateString = localDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const timeString = localDate
    .toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .toLocaleUpperCase();

  const timeZoneString =
    new Intl.DateTimeFormat(undefined, {
      timeZoneName: "shortGeneric",
    })
      .formatToParts(localDate)
      .find((part) => part.type === "timeZoneName")?.value ?? "";

  return `${dateString}, ${timeString} (${timeZoneString})`;
};

const getAssetFlow = (txn: AdminTransactionDetailFragment) => {
  const type = txn.type;
  const [FirstBadge, SecondBadge] = type
    ? transactionTypeToBadgeLabels[type]
    : [];

  let firstAsset: AssetCardFieldsFragment | undefined | null = undefined;
  let secondAsset: AssetCardFieldsFragment | undefined | null = undefined;
  let bene: BeneficiaryAssetCardFieldsFragment | undefined | null = undefined;
  let fxTrade: FxTradeCardFieldsFragment | undefined | null = undefined;

  const assetUrlBase = `/assets`;
  const beneUrlBase = `/beneficiary-accounts`;

  switch (txn.fundMovement?.__typename) {
    case "Deposit": {
      firstAsset = txn.fundMovement.destinationAsset;
      break;
    }
    case "Payout": {
      firstAsset = txn.fundMovement.sourceAsset;
      bene = txn.fundMovement.beneficiaryAccount;
      break;
    }
    case "Transfer": {
      firstAsset =
        txn.type === "TRANSFER_IN"
          ? txn.fundMovement.destinationAsset
          : txn.fundMovement.sourceAsset;
      secondAsset =
        txn.type === "TRANSFER_IN"
          ? txn.fundMovement.sourceAsset
          : txn.fundMovement.destinationAsset;
      break;
    }
    case "EburyFXTrade":
      if (
        txn.ledgerAccount?.__typename === "AssetLedgerAccount" &&
        txn.ledgerAccount.asset
      ) {
        firstAsset = txn.ledgerAccount.asset;
        fxTrade = txn.fundMovement;
      }
      break;
    case undefined: {
      firstAsset =
        txn.ledgerAccount?.__typename === "AssetLedgerAccount"
          ? txn.ledgerAccount.asset
          : undefined;
    }
  }

  return (
    <div className="flex flex-row gap-2">
      {(FirstBadge || firstAsset) && (
        <div className="flex min-w-0 flex-1 basis-0 flex-col">
          {FirstBadge && FirstBadge}
          {firstAsset && (
            <AssetCard
              asset={firstAsset}
              variant="COMPACT_CARD"
              linkTo={`${assetUrlBase}/${firstAsset.id}`}
            />
          )}
        </div>
      )}
      {(SecondBadge || secondAsset || bene) && (
        <div className="flex flex-1 basis-0 flex-col">
          {SecondBadge && SecondBadge}
          {secondAsset && (
            <AssetCard
              asset={secondAsset}
              variant="COMPACT_CARD"
              linkTo={`${assetUrlBase}/${secondAsset.id}`}
            />
          )}
          {bene && (
            <AssetCard
              beneficiaryAccount={bene}
              variant="COMPACT_CARD"
              linkTo={`${beneUrlBase}/${bene.id}`}
            />
          )}
          {fxTrade && <FXTradeCard fxTrade={fxTrade} />}
        </div>
      )}
    </div>
  );
};

const getBusinessEntityCard = (txn: AdminTransactionDetailFragment) => {
  let beName: string | undefined = undefined;
  let orgName: string | undefined = undefined;
  let logoUrl: string | undefined = undefined;
  let url: string = ".";

  if (
    txn.ledgerAccount?.__typename === "AssetLedgerAccount" &&
    txn.ledgerAccount.asset &&
    txn.ledgerAccount.asset?.businessEntity
  ) {
    const be = txn.ledgerAccount.asset?.businessEntity;
    beName = be.name;
    orgName = be.organisation?.name ?? "";
    logoUrl = be.organisation?.squareLogoUrl ?? "";
    url = `/organisations/${be.organisation?.id}/business-entities/${be.id}`;
  }

  return (
    <Link
      to={url}
      className="flex min-w-0 flex-row items-center gap-1 rounded-sm border border-zinc-200 p-2 shadow-sm hover:border-zinc-400"
    >
      {logoUrl ? (
        <img
          className="mr-2 size-8 shrink-0 rounded-sm"
          src={logoUrl}
          alt="Business entity logo"
        />
      ) : (
        <PiBuildingOffice className="mr-2 shrink-0 text-sm text-zinc-500" />
      )}
      <div className="flex min-w-0 flex-col">
        <Txt size="sm" as="div" className="truncate">
          {beName && beName}
        </Txt>
        <Txt size="sm" as="div" className="truncate" color="secondary">
          {orgName && orgName}
        </Txt>
      </div>
    </Link>
  );
};
